import React, { useState, useEffect } from "react";
import DSMobileStepper from "../system-design/DSMobileStepper";
import steps from "./Steps/steps";
import { useNavigate } from "react-router";
import { useHistory } from "../../utils";
import { WizardStep } from "../../consts/wizardSteps";
// eslint-disable-next-line import/named
import { SxProps, Theme, useTheme } from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CheckIcon from "@mui/icons-material/Check";
import DSPaper from "../system-design/DSPaper";
import DSAppBar from "../system-design/DSAppBar";
import DSTypography from "../system-design/Typography/DSTypography";
import DSBox from "../system-design/DSBox";
import { useGiftContext } from "./Context/GiftContextProvider";
import { KLYDO_YELLOW, KLYDO_BLACK } from "../../consts/colors";
import { isIOS, isMobile } from "react-device-detect";
import DSPrimaryIconButton from "../system-design/DSPrimaryIconButton";
import DSSecondaryIconButton from "../system-design/DSSecondaryIconButton";
import useDisableZoom from "../../hooks/useDisableZoom";
import useUpsertGiftMutation from "./hooks/useUpsertGiftMutation";
import { useNavigateDone } from "../../hooks/useNavigateDone";
import GreetLogo from "../system-design/GreetLogo";
import FinalConfirmationDialog from "./FinalConfirmationDialog";
import TermsDialog from "./TermsDialog";
import useUrlSyncedModal from "./hooks/useUrlSyncedModal";
import {
  StepValidationRule,
  useWizardNavigation,
} from "./hooks/useWizardNavigation";

const GiftWizard = () => {
  useDisableZoom();
  const { search } = useHistory();
  const orderName = search.orderName || search.orderId;
  const {
    selectedOccasion,
    klydoGiftProps,
    selectedKlydoGreet,
    loading,
    next,
    back,
    setKlydoGiftProps,
  } = useGiftContext();

  const step = search.step;
  const activeStep = steps.findIndex((s) => s.id === step);
  const { palette } = useTheme();
  const navigate = useNavigate();
  const navigateToDone = useNavigateDone();
  const { validateAndNavigate } = useWizardNavigation();

  const [finalDialogOpen, setFinalDialogOpen] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);

  const TERMS_PARAM_KEY = "terms";
  const TERMS_PARAM_VALUE = "open";

  const {
    open: termsModalOpen,
    onOpen: openTermsModal,
    onClose: closeTermsModal,
  } = useUrlSyncedModal(TERMS_PARAM_KEY, TERMS_PARAM_VALUE);

  const { mutate: addGiftMutation, isPending: addGiftPending } =
    useUpsertGiftMutation({
      onSuccess: () => {
        if (klydoGiftProps?.orderName) {
          navigateToDone({ orderName: klydoGiftProps.orderName });
          return;
        } else if (klydoGiftProps?.clockId) {
          navigateToDone({ clockId: klydoGiftProps.clockId });
          return;
        } else return;
      },
    });

  const handleTermsAcceptedChange = (accepted: boolean) => {
    if (accepted) {
      setShowTermsError(false);
      setKlydoGiftProps({
        ...klydoGiftProps,
        giftTerms: {
          accepted: true,
          signedTimestamp: Date.now(),
        },
      });
    } else {
      setKlydoGiftProps({
        ...klydoGiftProps,
        giftTerms: {
          accepted: false,
          signedTimestamp: null,
        },
      });
    }
  };

  const handleDone = () => {
    addGiftMutation(klydoGiftProps);
  };

  const resetDialogState = () => {
    setShowTermsError(false);
    setFinalDialogOpen(false);
  };

  const validateAndConfirm = () => {
    if (!klydoGiftProps?.giftTerms?.accepted) {
      setShowTermsError(true);
      return;
    }
    setFinalDialogOpen(false);
    handleDone();
  };

  useEffect(() => {
    const stepElement = document.getElementById("gift-wizard-step");
    if (stepElement) {
      stepElement.scrollTo(0, 0);
    }
  }, [search.step]);
  useEffect(() => {
    if (!klydoGiftProps?.clockId && !klydoGiftProps?.orderName) {
      navigate(
        `/gift${orderName ? `?orderName=${Array.isArray(orderName) ? orderName[0] : orderName}` : ""}`,
        { replace: true },
      );
      return;
    }

    const validationRules: StepValidationRule[] = [
      {
        stepIndex: 2,
        condition: activeStep >= 2 && !selectedKlydoGreet,
        redirectStep: WizardStep.GREET,
      },
      {
        stepIndex: 3,
        condition:
          activeStep >= 3 &&
          !klydoGiftProps?.senderName &&
          !klydoGiftProps?.pendulumLogoUrl,
        redirectStep: WizardStep.PERSONAL_GREET,
      },
    ];

    validateAndNavigate(validationRules, orderName);
  }, [
    activeStep,
    selectedOccasion,
    selectedKlydoGreet,
    klydoGiftProps?.senderName,
    klydoGiftProps?.clockId,
    klydoGiftProps?.orderName,
    orderName,
    navigate,
  ]);

  const handleButtonDone = () => {
    if (!klydoGiftProps?.senderName && !klydoGiftProps?.pendulumLogoUrl) {
      alert("Your name is required");
      return;
    }

    setFinalDialogOpen(true);
  };

  const handleNext = () => {
    if (
      (activeStep === 0 && selectedOccasion) ||
      (activeStep === 1 && selectedKlydoGreet) ||
      (activeStep === 2 &&
        (klydoGiftProps?.senderName || klydoGiftProps?.pendulumLogoUrl))
    ) {
      next();
    }
  };

  const handleBack = () => {
    back();
  };

  const buttonSx: SxProps<Theme> = {
    textTransform: "none",
    padding: 2,
    margin: 1,
    color: KLYDO_BLACK,
  };
  return (
    <>
      <FinalConfirmationDialog
        open={finalDialogOpen}
        onClose={resetDialogState}
        onConfirm={validateAndConfirm}
        onOpenTerms={openTermsModal}
        termsAccepted={klydoGiftProps?.giftTerms?.accepted}
        onTermsAcceptedChange={handleTermsAcceptedChange}
        showTermsError={showTermsError}
        loading={addGiftPending}
      />

      <TermsDialog open={termsModalOpen} onClose={closeTermsModal} />

      <DSPaper
        sx={{
          overflow: "hidden",
          minHeight: { xs: isIOS && isMobile ? 0 : "100vh", md: "100vh" },
        }}
      >
        <DSAppBar
          onClick={() => {
            window.location.href = "https://www.klydoclock.com/";
          }}
          position="fixed"
          color="transparent"
          sx={{
            cursor: "pointer",
            backgroundColor: palette.background.paper,
            paddingY: { xs: 2, md: 4 },
            display: "flex",
            justifyContent: "center",
            textAlign: "center",
            alignItems: "center",
            boxShadow: "none",
          }}
        >
          <GreetLogo color={KLYDO_YELLOW} />
        </DSAppBar>

        <DSBox
          id="gift-wizard-step"
          sx={{
            overflow: "auto",
            backgroundColor: palette.background.paper,
            paddingTop: { xs: 8, md: 16 },
            height: "100vh",
            paddingBottom: { xs: 10, md: 20 },
            display: "flex",
            flexDirection: "column",
            textAlign: "center",
            alignItems: "center",
            justifyContent: "start",
            overflowX: "hidden",
          }}
        >
          <DSTypography
            lineHeight={1}
            fontStyle={"bold"}
            fontSize={isMobile ? 35 : 47.52}
            fontFamily={"Gilroy-Bold"}
            maxWidth={isMobile ? "270px" : "1080px"}
          >
            {steps[activeStep]?.title}
          </DSTypography>
          <DSBox py={0.8} />
          <DSTypography
            fontSize={isMobile ? 15 : 18}
            fontFamily={"Gilroy-Medium"}
            maxWidth={isMobile ? "270px" : "1080px"}
          >
            {steps[activeStep]?.description}
          </DSTypography>
          {steps[activeStep]?.component}
        </DSBox>
        <DSMobileStepper
          variant="dots"
          position="bottom"
          steps={steps.length}
          activeStep={activeStep}
          sx={{
            width: { xs: "100%", md: 900 },
            margin: "auto",
            ".MuiMobileStepper-buttom": {
              backgroundColor: "#D9D9D9",
              margin: { xs: "5px" },
            },
            ".MuiMobileStepper-dot": {
              backgroundColor: "#D9D9D9",
              margin: { xs: "5px" },
            },
            ".MuiMobileStepper-dotActive": {
              backgroundColor: "#fac641",
            },
          }}
          nextButton={
            activeStep === 3 ? (
              <DSPrimaryIconButton
                disabled={addGiftPending}
                onClick={() => {
                  handleButtonDone();
                }}
                sx={{
                  ...buttonSx,
                  width: { xs: 20, md: "60px" },
                  height: { xs: 20, md: "60px" },
                }}
              >
                <CheckIcon sx={{ fontSize: { xs: 20, md: 30 } }} />
              </DSPrimaryIconButton>
            ) : (
              <DSPrimaryIconButton
                onClick={() => handleNext()}
                disabled={
                  (activeStep === 0 && !selectedOccasion) ||
                  (activeStep === 1 && !selectedKlydoGreet) ||
                  (activeStep === 2 &&
                    !klydoGiftProps?.senderName &&
                    !klydoGiftProps?.pendulumLogoUrl) ||
                  loading
                }
                sx={{
                  ...buttonSx,
                  width: { xs: 20, md: "60px" },
                  height: { xs: 20, md: "60px" },
                  "&:hover": {
                    backgroundColor: KLYDO_YELLOW,
                  },
                }}
              >
                <ArrowForwardIosIcon sx={{ fontSize: { xs: 20, md: 30 } }} />
              </DSPrimaryIconButton>
            )
          }
          backButton={
            <DSSecondaryIconButton
              onClick={() => handleBack()}
              disabled={activeStep === 0 || activeStep === 4 || !back}
              sx={{
                ...buttonSx,
                width: { xs: 20, md: "60px" },
                height: { xs: 20, md: "60px" },
              }}
            >
              <ArrowBackIosNewIcon sx={{ fontSize: { xs: 20, md: 30 } }} />
            </DSSecondaryIconButton>
          }
        />
      </DSPaper>
    </>
  );
};
export default GiftWizard;
